import { Box, ButtonBase, IconButton, Stack, Typography } from '@mui/material';
import { css, styled } from '@mui/material/styles';
import { CaretDoubleLeftIcon, CaretDoubleRightIcon, CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';

import type { AuditEntry } from '@/modules/api/backend';
import { transientOptions } from '@/utils/transient-options';

const TimelineContainer = styled(Box)`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacing(1)};
    flex: 1;
  `}
`;

const TimelineItem = styled(ButtonBase, transientOptions)<{ $selected?: boolean }>`
  ${({ theme, $selected }) => css`
    flex-direction: column;
    width: ${theme.spacing(16)};
    padding: ${theme.spacing(1, 2)};
    flex-shrink: 0;
    overflow: hidden;

    &:hover {
      background: ${theme.palette.action.hover};
    }

    ${$selected &&
    css`
      border: 2px solid ${theme.palette.primary.main};
    `}
  `}
`;

interface AuditNavigatorProps {
  entries: AuditEntry[];
  index: number;
  onChange: (newIndex: number) => void;
}

export function AuditNavigator({ entries, index, onChange }: AuditNavigatorProps) {
  return (
    <Stack
      alignItems="center"
      direction="row"
      flex={1}
      sx={{
        position: 'sticky',
        top: 184,
        zIndex: 100,
        backgroundColor: 'common.white',
        paddingTop: 2,
        paddingBottom: 2,
        borderBottom: '2px solid',
        borderBottomColor: 'primary.main',
        overflow: 'auto',
      }}
    >
      <TimelineContainer>
        <Box sx={{ position: 'sticky', left: 0, backgroundColor: 'common.white', zIndex: 1 }}>
          <IconButton
            disabled={index === 0}
            title="Vorherige Version"
            onClick={() => {
              onChange(index - 1);
            }}
          >
            <CaretLeftIcon />
          </IconButton>

          <IconButton
            disabled={index === 0}
            title="Ältestete Version"
            onClick={() => {
              onChange(0);
            }}
          >
            <CaretDoubleLeftIcon />
          </IconButton>
        </Box>

        {entries.map((entry, entryIndex) => {
          const selected = entryIndex === index;

          return (
            <TimelineItem
              key={entry.createdAt}
              $selected={selected}
              id={`audit-entry-${entryIndex}`}
              onClick={() => onChange(entryIndex)}
            >
              <Typography noWrap variant="body2">
                {new Date(entry.createdAt).toLocaleDateString()}
                <br />
                {new Date(entry.createdAt).toLocaleTimeString()}
              </Typography>
              <Typography noWrap color="text.secondary" textOverflow="ellipsis" variant="caption" width="100%">
                {entry.user ? `${entry.user.firstName} ${entry.user.lastName}` : 'System'}
              </Typography>
            </TimelineItem>
          );
        })}

        <Box sx={{ position: 'sticky', right: 0, backgroundColor: 'common.white', zIndex: 1 }}>
          <IconButton
            disabled={index === entries.length - 1}
            title="Nächste Version"
            onClick={() => {
              onChange(index + 1);
            }}
          >
            <CaretRightIcon />
          </IconButton>

          <IconButton
            disabled={index === entries.length - 1}
            title="Aktuelle Version"
            onClick={() => {
              onChange(entries.length - 1);
            }}
          >
            <CaretDoubleRightIcon />
          </IconButton>
        </Box>
      </TimelineContainer>
    </Stack>
  );
}
